/**
 * Privacy-Preserving Analytics Utility
 * 
 * PRIVACY GUARANTEES:
 * - No cookies
 * - No session IDs or user identifiers
 * - No IP or user agent storage
 * - No third-party tracking
 * - Only anonymous event payloads sent to first-party backend
 * 
 * Data collected:
 * - Event type (page_view, cta_click, etc.)
 * - Page path (no query params except UTMs)
 * - Landing path (memory only, not persisted)
 * - Referrer hostname only (not full URL)
 * - UTM parameters only
 */

import { API } from "@/App";

// In-memory storage (not persisted, resets on page refresh)
let landingPath = null;
let attribution = null;
let lastTrackedPath = null;
let debounceTimer = null;

// Debounce delay in ms
const DEBOUNCE_MS = 300;

/**
 * Extract hostname from a URL string
 * Returns null if invalid or same origin
 */
function extractHostname(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    // Don't track self-referrals
    if (parsed.hostname === window.location.hostname) return null;
    return parsed.hostname;
  } catch {
    return null;
  }
}

/**
 * Parse UTM parameters from URL
 * Only extracts utm_* params, ignores everything else
 */
function parseUTMParams() {
  const params = new URLSearchParams(window.location.search);
  const utms = {};
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  utmKeys.forEach(key => {
    const value = params.get(key);
    if (value) {
      // Sanitize: remove any PII patterns
      const sanitized = value.trim().slice(0, 100);
      if (!containsPII(sanitized)) {
        utms[key] = sanitized;
      }
    }
  });
  
  return utms;
}

/**
 * Check if a string might contain PII
 * Returns true if it looks like an email, IP, or UUID
 */
function containsPII(str) {
  if (!str) return false;
  // Email pattern
  if (/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(str)) return true;
  // IP pattern
  if (/\b(?:\d{1,3}\.){3}\d{1,3}\b/.test(str)) return true;
  // UUID pattern
  if (/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(str)) return true;
  return false;
}

/**
 * Initialize attribution data on first page load
 * Called once when the app starts
 */
export function initAnalytics() {
  // Set landing path (first path user sees) — cleaned so UUIDs in deep links
  // never reach the backend privacy validator (raw pathname caused 422s on
  // every subsequent page_view for the whole session)
  landingPath = getCleanPath();
  
  // Capture initial attribution
  attribution = {
    referrer_host: extractHostname(document.referrer),
    ...parseUTMParams()
  };
  
  // Track initial page view
  trackPageView();
}

/**
 * Get clean path without sensitive data
 */
function getCleanPath() {
  let path = window.location.pathname;
  
  // Remove UUIDs from path (could be user/contract IDs)
  path = path.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[id]');
  
  // Truncate to reasonable length
  return path.slice(0, 200);
}

/**
 * Send event to backend with retry
 */
async function sendEvent(payload) {
  const endpoint = `${API}/events`;
  
  // First attempt
  try {
    await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Don't send cookies
      credentials: 'omit'
    });
    return;
  } catch {
    // First attempt failed, try once more
  }
  
  // Retry once after short delay
  setTimeout(async () => {
    try {
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit'
      });
    } catch {
      // Drop silently - don't queue indefinitely
    }
  }, 1000);
}

/**
 * Track page view event
 * Debounced to prevent duplicates on rapid navigation
 */
export function trackPageView() {
  const currentPath = getCleanPath();
  
  // Skip if same path was just tracked (debounce)
  if (currentPath === lastTrackedPath) return;
  
  // Clear existing debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  
  // Debounce
  debounceTimer = setTimeout(() => {
    lastTrackedPath = currentPath;
    
    const payload = {
      event_type: 'page_view',
      path: currentPath,
      landing_path: landingPath,
      ...attribution
    };
    
    // Remove null/undefined values
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === undefined) {
        delete payload[key];
      }
    });
    
    sendEvent(payload);
  }, DEBOUNCE_MS);
}

/**
 * Track CTA click event
 * @param {string} ctaName - Name of the CTA (e.g., 'ask_cbaiq', 'get_started')
 */
export function trackCTAClick(ctaName) {
  if (!ctaName || containsPII(ctaName)) return;
  
  const payload = {
    event_type: 'feature_click',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  // Remove null/undefined values
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

/**
 * Track question submission event
 */
export function trackAskSubmit() {
  const payload = {
    event_type: 'question_asked',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  // Remove null/undefined values
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

/**
 * Track signup completion event
 */
export function trackSignupComplete() {
  const payload = {
    event_type: 'signup_complete',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  // Remove null/undefined values
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

/**
 * Track login event
 */
export function trackLogin() {
  const payload = {
    event_type: 'login',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

/**
 * Track contract upload event
 */
export function trackContractUpload() {
  const payload = {
    event_type: 'contract_upload',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

/**
 * Track PDF export event
 */
export function trackPDFExport() {
  const payload = {
    event_type: 'pdf_export',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

/**
 * Track pricing page view
 */
export function trackPricingView() {
  const payload = {
    event_type: 'pricing_view',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

/**
 * Track checkout start
 */
export function trackCheckoutStart() {
  const payload = {
    event_type: 'checkout_start',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

/**
 * Track checkout complete
 */
export function trackCheckoutComplete() {
  const payload = {
    event_type: 'checkout_complete',
    path: getCleanPath(),
    landing_path: landingPath,
    ...attribution
  };
  
  Object.keys(payload).forEach(key => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  sendEvent(payload);
}

// Export all tracking functions
export default {
  initAnalytics,
  trackPageView,
  trackCTAClick,
  trackAskSubmit,
  trackSignupComplete,
  trackLogin,
  trackContractUpload,
  trackPDFExport,
  trackPricingView,
  trackCheckoutStart,
  trackCheckoutComplete
};
